import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Modal, { ModalBody, ModalHeader, ModalHeaderTitle } from '@/components/common/Modal/Modal';
import NoData from '@/components/common/NoData/NoData';
import UserCheckbox from '@/components/common/UserCheckbox/UserCheckbox';
import { FQ_BUTTON_COLOR_VARIANTS, FQ_BUTTON_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import FQButton from '@/components/muiCustomized/FQButton/FQButton';
import { ExtendedUserData } from '@/domain/common';
import { MEDIA_QUERIES_BREAKPOINTS, PX_TO_REM } from '@/globalStyles';

import useEditApproversLogic from './useEditApproversLogic';

interface IEditApproversModalProps {
    className?: string;
    isModalOpen: boolean;
    onCloseModal: () => void;
    userApprovers?: ExtendedUserData[];
    allApprovers?: ExtendedUserData[];
    getUserApproversList: () => void;
    userId?: string;
    isReadOnly?: boolean;
}

const EditApproversModal = ({
    isModalOpen,
    onCloseModal,
    userApprovers,
    allApprovers,
    className,
    userId,
    getUserApproversList,
    isReadOnly,
}: IEditApproversModalProps) => {
    const { t } = useTranslation();
    const { isLoading, hasChanges, handleApproverChange, handleSubmit, isApproverSelected } = useEditApproversLogic({
        userId,
        userApprovers,
        isModalOpen,
        onCloseModal,
        getUserApproversList,
    });

    return (
        <Modal
            className={className}
            open={isModalOpen}
            title={t('coworkerApproversView_selectApprover_title')}
            onClose={onCloseModal}
        >
            <>
                {allApprovers ? (
                    allApprovers.map(approver => (
                        <UserCheckbox
                            key={approver.credentialId}
                            value={approver.credentialId}
                            name={`${approver.firstName} ${approver.lastName}`}
                            avatarUrl={approver.profilePictureUrl}
                            initialCheck={isApproverSelected(approver.credentialId)}
                            onChange={handleApproverChange}
                            disabled={isReadOnly}
                        />
                    ))
                ) : (
                    <NoData />
                )}

                <ButtonsWrapper>
                    <FQButton
                        data-variant={FQ_BUTTON_VARIANTS.fqOutline}
                        color={FQ_BUTTON_COLOR_VARIANTS.fqRed}
                        onClick={onCloseModal}
                        disabled={isLoading}
                    >
                        {t('alert_defaultDeclineAction_title')}
                    </FQButton>

                    {!isReadOnly && (
                        <FQButton
                            data-variant={FQ_BUTTON_VARIANTS.fqPrimary}
                            color={FQ_BUTTON_COLOR_VARIANTS.fqRed}
                            disabled={isLoading || !hasChanges}
                            onClick={handleSubmit}
                        >
                            {t('editUserDetailsView_saveButton_title')}
                        </FQButton>
                    )}
                </ButtonsWrapper>
            </>
        </Modal>
    );
};

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: ${PX_TO_REM[20]};
    gap: ${PX_TO_REM[10]};

    > button {
        flex: 1;
        max-width: 12.5rem;
    }

    @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.mMobile}px) {
        flex-direction: column;

        > button {
            max-width: none;
        }
    }
`;

const Memoized = memo(EditApproversModal);
const Styled = styled(Memoized)`
    ${ModalBody} {
        padding: 0;
        width: clamp(16rem, 80vw, 26rem);
        box-sizing: border-box;
    }

    ${ModalHeader} {
        padding-left: ${PX_TO_REM[20]};
        padding-right: ${PX_TO_REM[20]};

        .MuiSvgIcon-root {
            font-size: ${PX_TO_REM[22]};
        }
    }

    ${ModalHeaderTitle} {
        align-items: flex-start;
        text-align: left;

        .MuiTypography-root {
            font-size: 1rem;
        }
    }

    .MuiTypography-root {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

export default Styled;
